import { useEffect, useState } from "react";
import { LuExternalLink, LuLoaderCircle } from "react-icons/lu";
import { useIsUserSignedIn } from "../../../core_stores/useAuthStore";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import {
  useSubscriptionActions,
  useUserSubscription,
} from "../../../core_stores/useSubscriptionStore";
import { useSubscriptionsModal } from "../../../providers/SubscriptionsModalProvider";
import { formatDateFromDDMMYYYY } from "../../../utils/dateFormatter";
import { formatTokensNumber } from "../../../utils/numberFormatter";
import { useArePersonalSettingsOpen } from "../../../view_stores/useSideMenuStore";

export default function PersonalSettingsPlans() {
  return (
    <div className="text-light-text dark:text-dark-text flex flex-col">
      <PlanOverview />
      <hr className="border-[#8B8B8B4D] my-5" />
      <div className="flex flex-col">
        <div className="w-full">
          <TokensUsage />
        </div>
        <hr className="border-[#8B8B8B4D] my-5" />
        <div className="w-full">
          <BillingPeriod />
        </div>
      </div>
      <hr className="border-[#8B8B8B4D] my-5" />
      <RedirectToUserPanel />
    </div>
  );
}

function PlanOverview() {
  const isCompactView = useIsCompactView();
  const { open: openSubscriptionsModal } = useSubscriptionsModal();
  const subscription = useUserSubscription();

  return (
    <div
      className={`${
        isCompactView && "flex-col gap-4"
      } flex items-center justify-between`}
    >
      <div className="flex flex-col gap-2">
        <div className="flex gap-2 items-center">
          <span className="font-semibold">Your Plan</span>
          <span className="text-white font-semibold bg-primary-blue rounded-full px-3 py-0.5">
            {subscription?.plan ?? ""}
          </span>
        </div>
      </div>
      <button
        id="personal-settings-plans-upgrade-plan"
        onClick={openSubscriptionsModal}
        className={`${
          isCompactView && "w-full"
        } bg-gradient-to-r from-fuchsia-500 to-violet-500 
        text-white font-semibold px-5 py-2 
            rounded-lg shadow hover:opacity-90 transition`}
      >
        Upgrade Plan
      </button>
    </div>
  );
}

function TokensUsage() {
  const arePersonalSettingsOpen = useArePersonalSettingsOpen();
  const isUserSignedIn = useIsUserSignedIn();
  const subscription = useUserSubscription();
  const { reloadSubscriptionsInfo } = useSubscriptionActions();

  useEffect(() => {
    if (isUserSignedIn && arePersonalSettingsOpen) {
      reloadSubscriptionsInfo();
    }
  }, [isUserSignedIn, arePersonalSettingsOpen]);

  const tokensLimit = subscription ? Number(subscription.limit) : undefined;
  const tokensUsed = subscription ? subscription.used : undefined;
  const formattedTokensLimit =
    tokensLimit === undefined ? "N/A" : formatTokensNumber(tokensLimit);
  const formattedTokensRemaining =
    subscription === null ? "N/A" : formatTokensNumber(subscription.remaining);
  const formattedTokensUsed =
    tokensUsed === undefined ? "N/A" : formatTokensNumber(tokensUsed);
  const usageBarWidth = subscription ? (tokensUsed! / tokensLimit!) * 100 : 0;

  return (
    <div className="flex flex-col gap-[6px]">
      <div className="font-semibold">Token Usage</div>
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className="font-[300]">Monthly tokens</span>
          <span>
            {formattedTokensUsed} / {formattedTokensLimit}
          </span>
        </div>
        <div
          className="w-full h-2 bg-[#8B8B8B4D]
          rounded-full overflow-hidden mb-1"
        >
          <div
            className="h-full bg-primary-blue rounded-full transition-all"
            style={{
              width: `${usageBarWidth >= 0 ? usageBarWidth : 0}%`,
            }}
          />
        </div>
        <div className="font-[300] flex items-center justify-between">
          Current balance
          <span className="font-semibold">{formattedTokensRemaining}</span>
        </div>
      </div>
    </div>
  );
}

function BillingPeriod() {
  const subscription = useUserSubscription();

  return (
    <div className="w-full flex flex-col gap-[6px]">
      <div className="font-semibold">Billing Period</div>
      <div className="flex flex-col gap-1 font-[300]">
        <div className="flex items-center justify-between">
          <div>Start</div>
          <div>
            {subscription?.period.start
              ? formatDateFromDDMMYYYY(subscription.period.start)
              : "N/A"}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div>End</div>
          <div>
            {subscription?.period.end
              ? formatDateFromDDMMYYYY(subscription.period.end)
              : "N/A"}
          </div>
        </div>
      </div>
    </div>
  );
}

function RedirectToUserPanel() {
  const { navigateToUserPanel } = useSubscriptionActions();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const onClick = async () => {
    setIsRedirecting(true);
    await navigateToUserPanel();
    setIsRedirecting(false);
  };

  return (
    <button
      id="personal-settings-plans-redirect-to-user-panel"
      onClick={onClick}
      className="text-primary-blue dark:text-blue-400 
        text-[min(14px,3.2vw)] hover:underline bg-transparent border-none 
        cursor-pointer p-0 flex justify-center items-center gap-1"
      disabled={isRedirecting}
    >
      Manage your payment preferences and check past payments
      <LuExternalLink />
      {isRedirecting && <LuLoaderCircle className="animate-spin" />}
    </button>
  );
}
